import { Spinner } from "@/components/Spinner"
import { ApiError } from "@/lib/api"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { type FormEvent, useEffect, useRef, useState } from "react"
import { type EnvioDeAlegacao, enviarAlegacao } from "../api"
import type { Repasse } from "../schema"
import { CampoArquivo } from "./CampoArquivo"
import { NotaSegura } from "./NotaSegura"
import { FOCO, levarAte } from "./foco"

const BOTAO = `min-h-11 rounded-full bg-brand px-6 py-2.5 font-display font-semibold text-brand-ink transition-colors hover:bg-brand-strong disabled:opacity-60 ${FOCO}`

const mensagemDe = (erro: unknown) =>
    erro instanceof ApiError && erro.mensagem
        ? erro.mensagem
        : "Não conseguimos registrar o envio. Recarregue a página: se os contratos aparecerem como Em análise, está feito."

interface Props {
    repasse: Repasse
    caminho: string
}

const Formulario = ({ repasse, caminho, onEnviada }: Props & { onEnviada: () => void }) => {
    const queryClient = useQueryClient()
    const [problema, setProblema] = useState<string | null>(null)
    const [relatorios, setRelatorios] = useState<File[]>([])
    const [comprovantes, setComprovantes] = useState<File[]>([])

    const mutation = useMutation({
        mutationFn: (envio: EnvioDeAlegacao) => enviarAlegacao(repasse.id, envio),
        // Reset, e não invalidate: o repasse volta ao esqueleto até chegar com os contratos em análise.
        onSuccess: () => {
            onEnviada()
            void queryClient.resetQueries({ queryKey: ["repasse", caminho] })
        },
    })

    const enviar = (evento: FormEvent<HTMLFormElement>) => {
        evento.preventDefault()
        const [relatorio] = relatorios
        setProblema(relatorio ? null : "Falta o relatório de empréstimos consignados.")
        if (!relatorio) return
        mutation.mutate({ relatorio, comprovantes })
    }

    const mensagem = problema ?? (mutation.isError ? mensagemDe(mutation.error) : null)

    return (
        <form
            id="alegacao"
            aria-labelledby="chamada-alegacao"
            tabIndex={-1}
            noValidate
            onSubmit={enviar}
            className="mt-5 flex flex-col gap-6 outline-none"
        >
            <CampoArquivo
                nome="relatorio"
                rotulo="Relatório de empréstimos consignados"
                maximo={1}
                arquivos={relatorios}
                onMudar={setRelatorios}
                ajuda={
                    <>
                        No FGTS Digital, entre em <b>Gestão de Guias › Consulta de Guias</b>, clique
                        em <b>Detalhar</b> na guia paga, depois em <b>Imprimir relatório PDF</b>, e
                        abra a aba <b>Consignado</b>. É o relatório que lista CPF, número do
                        contrato e instituição de cada colaborador.
                    </>
                }
            />
            <CampoArquivo
                nome="comprovante"
                rotulo="Comprovante Guia FGTS (opcional)"
                ajuda="A guia do FGTS Digital paga, com a autenticação bancária, ou o comprovante do Pix."
                maximo={2}
                arquivos={comprovantes}
                onMudar={setComprovantes}
            />

            <NotaSegura>
                Os arquivos trazem dados de contratos dos seus colaboradores, inclusive de outras
                instituições. Usamos só para conferir o repasse, conforme a LGPD.
            </NotaSegura>

            {mensagem && (
                <p role="alert" className="rounded-lg bg-off-wash px-4 py-3 text-sm text-off">
                    {mensagem}
                </p>
            )}

            <button
                type="submit"
                disabled={mutation.isPending}
                aria-busy={mutation.isPending}
                className={`inline-flex items-center gap-2 self-start ${BOTAO}`}
            >
                {mutation.isPending && <Spinner />}
                Enviar para análise
            </button>
        </form>
    )
}

export const ChamadaAlegacao = ({ repasse, caminho, abrir }: Props & { abrir: boolean }) => {
    const [aberta, setAberta] = useState(abrir)
    const secao = useRef<HTMLElement>(null)

    useEffect(() => {
        if (abrir) levarAte(secao.current, secao.current?.querySelector("form") ?? null)
    }, [abrir])

    if (!repasse.podeAlegar) return null

    return (
        <section
            ref={secao}
            aria-labelledby="chamada-alegacao"
            className="border-b border-line bg-brand-wash px-6 py-4"
        >
            <h2 id="chamada-alegacao" className="font-semibold">
                Repassou e não identificamos?
            </h2>
            <button
                type="button"
                aria-expanded={aberta}
                aria-controls="alegacao"
                onClick={() => setAberta(!aberta)}
                className={`mt-3 ${BOTAO}`}
            >
                Envie sua escrituração aqui
            </button>
            {aberta && (
                <Formulario
                    repasse={repasse}
                    caminho={caminho}
                    onEnviada={() => setAberta(false)}
                />
            )}
        </section>
    )
}
